import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { featureFlags } from "@/config/featureFlags";
import { agentService, appService, flowService } from "@/lib/services";
import type { Agent, App, FlowSummary, TeamHub as TeamHubType } from "@/types";
import { QuickActionsSection } from "./QuickActionsManager";
import { TaskTemplatesSection } from "./TaskTemplatesManager";
import { TeamAppsSection } from "./TeamAppsManager";

export function SettingsTab({
  hub,
  navigate,
  onRefresh,
}: Readonly<{
  hub: TeamHubType;
  navigate: ReturnType<typeof useNavigate>;
  onRefresh: () => void;
}>) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [apps, setApps] = useState<App[]>([]);
  const [flows, setFlows] = useState<FlowSummary[]>([]);

  useEffect(() => {
    // Load agents available for this team (for dropdowns)
    agentService
      .list({ pageSize: 100 })
      .then((res) => setAgents(res.data ?? []))
      .catch(() => {});
    // Load all apps (for dropdowns)
    appService
      .list()
      .then((res) => setApps(res.data ?? []))
      .catch(() => {});
    // Load all flows (for dropdowns)
    flowService
      .list({ pageSize: 100 })
      .then((res) => setFlows(res.data ?? []))
      .catch(() => {});
  }, []);

  return (
    <div className="p-6 max-w-3xl space-y-6">
      {/* Hub Content Management */}
      <div>
        <h2 className="text-lg font-semibold text-content-primary mb-1">Hub Content</h2>
        <p className="text-sm text-content-tertiary mb-4">
          Manage the quick actions, task templates, and apps shown on this team's hub page.
        </p>
        <div className="space-y-4">
          <QuickActionsSection hub={hub} agents={agents} apps={apps} flows={flows} onRefresh={onRefresh} />
          {featureFlags.autopilot && <TaskTemplatesSection hub={hub} agents={agents} onRefresh={onRefresh} />}
          {featureFlags.apps && <TeamAppsSection hub={hub} onRefresh={onRefresh} />}
        </div>
      </div>

      {/* Team Management link */}
      <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
        <h3 className="text-lg font-semibold text-content-primary mb-2">Team Management</h3>
        <p className="text-sm text-content-tertiary mb-4">
          Edit team settings, manage members, and configure permissions.
        </p>
        <button
          onClick={() => navigate(`/teams/${hub.uuid}/manage`)}
          className="px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
        >
          Open Team Management
        </button>
      </div>

      {/* Team Info */}
      <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
        <h3 className="text-sm font-medium text-content-secondary mb-2">Team Info</h3>
        <dl className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <dt className="text-content-tertiary">Created</dt>
            <dd className="text-content-primary">{new Date(hub.createdAt).toLocaleDateString()}</dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-content-tertiary">Your role</dt>
            <dd className="text-content-primary capitalize">{hub.currentUserRole?.replace("_", " ") || "N/A"}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
